import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import App from './App';
import './index.css';
import './design-system/ds.css';
import './design-system/palettes.css';
import './i18n';
import { makeAuthFetch } from './lib/authFetch';
import { makeWorkspaceFetch } from './modules/files/workspaceFetch';
import { makeOpenAppsFetch } from './lib/openApps';
import { installClientErrorBuffer } from './lib/clientErrorBuffer';
// Side-effect import: installs the auth subscription that clears notification
// state on sign-out / user switch so one user's alerts, run logs, actions and
// approvals never leak into the next session.
import './store/sessionReset';

// Error tracking — only active when VITE_SENTRY_DSN is baked in at build
// time (prod builds). sendDefaultPii stays false: no cookies, headers, or
// user values leave the platform (SOC 2 confidentiality / ISO 27001 A.8.11).
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    sendDefaultPii: false,
  });
}

// Global fetch interceptor: inject x-tenant-id and Authorization into every fetch call.
// This covers all direct fetch() calls in stores/components that hardcode 'tenant-001'.
//
// Skipped entirely when share-mode is active: the public viewer hits token-scoped
// endpoints and must not leak tenant credentials. lib/shareMode installs its own
// rewriter that injects X-Share-Session for those requests.
// Injects x-tenant-id + Authorization, and silently refreshes + retries once on
// a 401 (expired access token). See lib/authFetch for the rationale.
// Aggregates also name the dashboards on screen (lib/openApps), so one shared
// with you keeps working where your profile does not include its type.
window.fetch = makeOpenAppsFetch(makeWorkspaceFetch(makeAuthFetch(window.fetch.bind(window))));

// Ring buffer of recent console/uncaught errors — attached to issue reports
// (Report an issue drawer) so triage can cross-reference client and server.
installClientErrorBuffer();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
